import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCcw, Settings2, Sparkles, Database, ExternalLink, Link2, Wrench, Trash2 } from 'lucide-react';
import { adminReportingAPI } from '../api';
import { ACTIVE_AUTOMATION_PROFILE_CONTEXT_KEY, LINKTRUST_SESSION_CREDENTIALS_KEY } from '../utils/linktrustImport';

const views = [
  { id: 'campaigns', label: 'Campaigns' },
  { id: 'bulk-links', label: 'Bulk Links' },
  { id: 'advertisers', label: 'Advertisers' },
  { id: 'affiliates', label: 'Affiliates' },
  { id: 'queue', label: 'Outbound Queue' }
];

const campaignStatusOptions = ['backlog', 'queued', 'in_progress', 'creative_build', 'qa', 'ready_to_launch', 'live', 'paused', 'completed', 'archived'];
const linkTrustCampaignStatusOptions = ['Testing', 'PublicLive', 'PrivateLive', 'Paused', 'Dead'];

const toDisplayError = (error, fallbackMessage) => {
  const detailRows = Array.isArray(error?.details) ? error.details : [];
  if (!detailRows.length) return error?.message || fallbackMessage;
  const firstDetail = detailRows[0];
  if (typeof firstDetail === 'string') return `${error.message || fallbackMessage}: ${firstDetail}`;
  return `${error.message || fallbackMessage}: ${firstDetail?.msg || firstDetail?.message || JSON.stringify(firstDetail)}`;
};

const parseCredentialSession = () => {
  try {
    const raw = sessionStorage.getItem(LINKTRUST_SESSION_CREDENTIALS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.username || !parsed?.password) return null;
    return parsed;
  } catch {
    return null;
  }
};

const parseActiveProfileContext = () => {
  try {
    const raw = sessionStorage.getItem(ACTIVE_AUTOMATION_PROFILE_CONTEXT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !parsed.profileId) return null;
    return parsed;
  } catch {
    return null;
  }
};

const Management = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [activeView, setActiveView] = useState('campaigns');

  const [merchantId, setMerchantId] = useState('');
  const [automationProfiles, setAutomationProfiles] = useState([]);
  const [selectedAutomationProfileId, setSelectedAutomationProfileId] = useState('');
  const [activeProfileContext, setActiveProfileContext] = useState(() => parseActiveProfileContext());
  const [merchantOptions, setMerchantOptions] = useState([]);
  const [campaignRows, setCampaignRows] = useState([]);
  const [advertiserRows, setAdvertiserRows] = useState([]);
  const [affiliateRows, setAffiliateRows] = useState([]);
  const [merchantSettings, setMerchantSettings] = useState(null);
  const [hasLoadedMerchants, setHasLoadedMerchants] = useState(false);

  const [search, setSearch] = useState('');
  const [campaignAdvertiserFilter, setCampaignAdvertiserFilter] = useState('all');
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [selectedAdvertiserKey, setSelectedAdvertiserKey] = useState('');
  const [selectedAffiliateId, setSelectedAffiliateId] = useState(null);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState([]);
  const [selectedAffiliateIds, setSelectedAffiliateIds] = useState([]);

  const [campaignDetailsEdit, setCampaignDetailsEdit] = useState({
    campaignName: '',
    category: '',
    campaignType: 'CPA',
    defaultLandingPageUrl: '',
    campaignStatus: 'Testing'
  });
  const [campaignLinks, setCampaignLinks] = useState([]);
  const [campaignCreatives, setCampaignCreatives] = useState([]);
  const [loadingCampaignAssets, setLoadingCampaignAssets] = useState(false);
  const [bulkCampaignCreatives, setBulkCampaignCreatives] = useState([]);
  const [bulkSavedLinks, setBulkSavedLinks] = useState([]);
  const [bulkLinkOptions, setBulkLinkOptions] = useState({ includeBaseLinks: true, includeAdIds: true });
  const [showLinkCampaignContext, setShowLinkCampaignContext] = useState(true);
  const [showLinkAdContext, setShowLinkAdContext] = useState(true);
  const [showCampaignMassActions, setShowCampaignMassActions] = useState(false);

  const [linkPreview, setLinkPreview] = useState([]);
  const [generatingLinks, setGeneratingLinks] = useState(false);
  const [rebuildingAdvertisers, setRebuildingAdvertisers] = useState(false);
  const [syncingCampaignAdvertisers, setSyncingCampaignAdvertisers] = useState(false);
  const [pushingLinkTrustStatuses, setPushingLinkTrustStatuses] = useState(false);
  const [savingCampaignStatuses, setSavingCampaignStatuses] = useState(false);
  const [savingCampaignDetails, setSavingCampaignDetails] = useState(false);
  const [deletingCampaigns, setDeletingCampaigns] = useState(false);
  const [savingAdvertiser, setSavingAdvertiser] = useState(false);
  const [savingAffiliate, setSavingAffiliate] = useState(false);
  const [addingCreative, setAddingCreative] = useState(false);

  const [campaignBulkEdit, setCampaignBulkEdit] = useState({ workspaceStatus: 'in_progress', desiredLinkTrustStatus: 'Testing', ownerName: '', notes: '', queueLinkTrustSync: false });
  const [advertiserEdit, setAdvertiserEdit] = useState({ accountStatus: 'active', ownerName: '', tags: '', notes: '', syncToLinkTrust: false, queueLinkTrustSync: false });
  const [affiliateEdit, setAffiliateEdit] = useState({ partnerStatus: 'active', ownerName: '', tags: '', notes: '', syncToLinkTrust: false, queueLinkTrustSync: false });
  const [creativeForm, setCreativeForm] = useState({
    creativeName: '',
    creativeType: '1',
    adId: '',
    content: '',
    sizeId: '',
    enabled: true,
    destinationUrl: '',
    propagateAdditionalData: false,
    imageUrl: '',
    affiliatePayoutType: '',
    affiliatePayout: '',
    advertiserRevenue: '',
    enableForAllAffiliates: false,
    affiliateGroupId: '',
    showTrackingLinkInListing: false,
    displaySubjectInAC: false,
    downloadOnlyForAllAffiliates: false,
    groupId: '',
    subject: '',
    affiliates: ''
  });

  const credentials = useMemo(() => parseCredentialSession(), []);
  const selectedMerchantId = useMemo(() => {
    const parsed = Number.parseInt(String(merchantId || '').trim(), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [merchantId]);

  const selectedCampaign = useMemo(() => campaignRows.find((row) => Number(row.linktrust_campaign_id) === Number(selectedCampaignId)) || null, [campaignRows, selectedCampaignId]);
  const selectedAdvertiser = useMemo(() => advertiserRows.find((row) => `${row.merchant_id}:${row.advertiser_id || 'unknown'}:${(row.company_name || row.advertiser_name || '').toLowerCase()}` === selectedAdvertiserKey) || null, [advertiserRows, selectedAdvertiserKey]);
  const selectedAffiliate = useMemo(() => (merchantSettings?.management?.affiliates || affiliateRows).find((row) => Number(row.affiliate_id) === Number(selectedAffiliateId)) || null, [merchantSettings, affiliateRows, selectedAffiliateId]);
  const toCampaignId = (row) => Number.parseInt(String(row?.linktrust_campaign_id || ''), 10);
  const campaignNameById = useMemo(() => {
    const map = new Map();
    campaignRows.forEach((row) => {
      const campaignId = Number.parseInt(String(row?.linktrust_campaign_id || ''), 10);
      if (Number.isFinite(campaignId) && campaignId > 0 && !map.has(campaignId)) {
        map.set(campaignId, row.campaign_name || `Campaign ${campaignId}`);
      }
    });
    return map;
  }, [campaignRows]);

  const renderLinkRows = (items = [], { useSavedUrl = false, useSavedAdId = false, keyPrefix = 'link' } = {}) => (
    items.map((item, index) => {
      const campaignId = Number.parseInt(String(item.campaign_id || item.campaignId || ''), 10);
      const adId = useSavedAdId
        ? Number.parseInt(String(item.ad_id || ''), 10)
        : Number.parseInt(String(item.adId || ''), 10);
      const url = useSavedUrl ? item.generated_url : item.url;
      const rowKey = item.id || `${keyPrefix}-${index}-${campaignId || 'na'}-${adId || 'base'}`;
      const campaignName = campaignNameById.get(campaignId) || (campaignId ? `Campaign ${campaignId}` : 'Unknown campaign');

      return (
        <div key={rowKey} className="mb-2">
          {showLinkCampaignContext && <p className="text-[11px] text-slate-400">{campaignName}{campaignId ? ` (${campaignId})` : ''}</p>}
          {showLinkAdContext && <p className="text-[11px] text-slate-500">{adId ? `Ad/Creative: ADID ${adId}` : 'Base campaign link'}</p>}
          <p className="text-[11px] text-slate-300 break-all">{url}</p>
        </div>
      );
    })
  );

  const campaignAdvertiserOptions = useMemo(() => {
    const map = new Map();
    campaignRows.forEach((row) => {
      const advertiserId = row.advertiser_id ? String(row.advertiser_id) : 'unknown';
      const advertiserName = row.advertiser_name || 'Unmapped advertiser';
      const key = `${advertiserId}|${advertiserName.toLowerCase()}`;
      if (!map.has(key)) {
        map.set(key, { value: key, label: `${advertiserName}${advertiserId !== 'unknown' ? ` (${advertiserId})` : ''}` });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [campaignRows]);

  const advertiserCountForHeader = useMemo(() => {
    if (activeView === 'campaigns' || activeView === 'bulk-links') return campaignAdvertiserOptions.length;
    return advertiserRows.length;
  }, [activeView, campaignAdvertiserOptions.length, advertiserRows.length]);

  const filteredRows = useMemo(() => {
    const query = String(search || '').trim().toLowerCase();
    const hay = (value) => String(value || '').toLowerCase();
    if (activeView === 'campaigns' || activeView === 'bulk-links') {
      return campaignRows.filter((row) => {
        const advertiserKey = `${row.advertiser_id ? String(row.advertiser_id) : 'unknown'}|${String(row.advertiser_name || 'Unmapped advertiser').toLowerCase()}`;
        const advertiserFilterMatch = campaignAdvertiserFilter === 'all' || advertiserKey === campaignAdvertiserFilter;
        const queryMatch = !query || [row.campaign_name, row.linktrust_campaign_id, row.advertiser_name, row.workspace_status, row.category, row.campaign_status].some((v) => hay(v).includes(query));
        return advertiserFilterMatch && queryMatch;
      });
    }
    if (activeView === 'advertisers') {
      return advertiserRows.filter((row) => !query || [row.advertiser_name, row.company_name, row.advertiser_id, row.email].some((v) => hay(v).includes(query)));
    }
    if (activeView === 'affiliates') {
      const rows = merchantSettings?.management?.affiliates || affiliateRows;
      return rows.filter((row) => !query || [row.affiliate_name, row.affiliate_id, row.email, row.partner_status].some((v) => hay(v).includes(query)));
    }
    return merchantSettings?.outboundQueue || [];
  }, [activeView, campaignRows, advertiserRows, affiliateRows, merchantSettings, search, campaignAdvertiserFilter]);

  const persistProfileContext = (context) => {
    if (!context?.profileId) return;
    sessionStorage.setItem(ACTIVE_AUTOMATION_PROFILE_CONTEXT_KEY, JSON.stringify(context));
    setActiveProfileContext(context);
  };

  const loadAutomationProfiles = async () => {
    const response = await adminReportingAPI.listLinkTrustAutomationProfiles({ limit: 500 });
    const profiles = response?.data?.profiles || [];
    setAutomationProfiles(profiles);
    return profiles;
  };

  const applyAutomationProfileContext = async (profileId) => {
    if (!profileId) return;
    const response = await adminReportingAPI.getLinkTrustAutomationProfile(profileId);
    const profile = response?.data?.profile;
    if (!profile) throw new Error('Selected automation profile not found.');
    const merchant = String(
      profile?.credentials?.merchantId
      || profile?.merchant_id
      || profile?.profile_payload?.syncContext?.merchantId
      || ''
    ).replace(/[^0-9]/g, '');
    if (!merchant) {
      throw new Error('Selected profile does not have a merchant context. Save the profile with a merchant ID first.');
    }
    setSelectedAutomationProfileId(String(profile.id));
    setMerchantId(merchant);
    persistProfileContext({
      profileId: String(profile.id),
      profileName: profile.profile_name || '',
      clientName: profile.client_name || '',
      merchantId: merchant,
      username: profile?.credentials?.username || '',
      savedAt: new Date().toISOString()
    });
  };

  const fetchWorkspace = async ({ soft = false } = {}) => {
    try {
      if (soft) setRefreshing(true);
      else setLoading(true);
      setError('');

      if (!hasLoadedMerchants) {
        const merchantResp = await adminReportingAPI.listLinkTrustMerchantOptions();
        const merchants = merchantResp?.data?.merchants || [];
        setMerchantOptions(merchants);
        setHasLoadedMerchants(true);
        if (!selectedMerchantId && merchants.length) {
          setMerchantId(String(merchants[0].merchantId));
          return;
        }
      }

      if (!selectedMerchantId) {
        setCampaignRows([]);
        setAdvertiserRows([]);
        setAffiliateRows([]);
        setMerchantSettings(null);
        return;
      }

      const merchantParam = { merchant_id: selectedMerchantId };
      if (activeView === 'campaigns' || activeView === 'bulk-links') {
        const [campaignResp, affiliateResp] = await Promise.all([
          adminReportingAPI.listLinkTrustCampaignManagement(merchantParam),
          adminReportingAPI.listLinkTrustAffiliates({ ...merchantParam, limit: 1500 })
        ]);
        const rows = campaignResp?.data?.campaigns || [];
        setCampaignRows(rows);
        setAffiliateRows(affiliateResp?.data?.affiliates || []);
        if (selectedCampaignId && !rows.some((row) => Number(row.linktrust_campaign_id) === Number(selectedCampaignId))) {
          setSelectedCampaignId(null);
        }
        return;
      }

      if (activeView === 'advertisers') {
        const [advertiserResp, settingsResp] = await Promise.all([
          adminReportingAPI.listLinkTrustAdvertisers(merchantParam),
          adminReportingAPI.getLinkTrustAdvertiserSettings(selectedMerchantId)
        ]);
        setAdvertiserRows(advertiserResp?.data?.advertisers || []);
        setMerchantSettings(settingsResp?.data || null);
        return;
      }

      if (activeView === 'affiliates') {
        const [affiliateResp, settingsResp] = await Promise.all([
          adminReportingAPI.listLinkTrustAffiliates({ ...merchantParam, limit: 1500 }),
          adminReportingAPI.getLinkTrustAdvertiserSettings(selectedMerchantId)
        ]);
        setAffiliateRows(affiliateResp?.data?.affiliates || []);
        setMerchantSettings(settingsResp?.data || null);
        return;
      }

      if (activeView === 'queue') {
        const settingsResp = await adminReportingAPI.getLinkTrustAdvertiserSettings(selectedMerchantId);
        setMerchantSettings(settingsResp?.data || null);
      }
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Failed to load management workspace'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const profiles = await loadAutomationProfiles();
        if (!active) return;
        const saved = parseActiveProfileContext();
        if (saved?.profileId) {
          setSelectedAutomationProfileId(String(saved.profileId));
          if (saved.merchantId) {
            setMerchantId(String(saved.merchantId));
          }
          setActiveProfileContext(saved);
          return;
        }
        if (profiles.length > 0 && !selectedAutomationProfileId) {
          const first = profiles[0];
          if (first?.id) setSelectedAutomationProfileId(String(first.id));
        }
      } catch {
        // profile loading should not block workspace
      }
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    fetchWorkspace();
  }, [selectedMerchantId, activeView]);

  useEffect(() => {
    const row = merchantSettings?.management?.advertiser;
    if (!row) return;
    setAdvertiserEdit((state) => ({
      ...state,
      accountStatus: row.account_status || 'active',
      ownerName: row.owner_name || '',
      tags: row.tags || '',
      notes: row.notes || '',
      syncToLinkTrust: row.sync_to_linktrust === true
    }));
  }, [merchantSettings]);

  useEffect(() => {
    if (!selectedCampaign) {
      setCampaignLinks([]);
      setCampaignCreatives([]);
      return;
    }

    setCampaignDetailsEdit({
      campaignName: selectedCampaign.campaign_name || '',
      category: selectedCampaign.category || '',
      campaignType: selectedCampaign.campaign_type || 'CPA',
      defaultLandingPageUrl: selectedCampaign.default_landing_page_url || '',
      campaignStatus: selectedCampaign.campaign_status || 'Testing'
    });

    const loadCampaignAssets = async () => {
      if (!selectedMerchantId || !selectedCampaign?.linktrust_campaign_id) return;
      try {
        setLoadingCampaignAssets(true);
        const [linksResp, creativesResp] = await Promise.all([
          adminReportingAPI.listLinkTrustLinks({ merchant_id: selectedMerchantId, campaign_id: selectedCampaign.linktrust_campaign_id, limit: 200 }),
          adminReportingAPI.listLinkTrustCreatives({ merchant_id: selectedMerchantId, campaign_id: selectedCampaign.linktrust_campaign_id, limit: 300 })
        ]);
        setCampaignLinks(linksResp?.data?.links || []);
        setCampaignCreatives(creativesResp?.data?.creatives || []);
      } catch (requestError) {
        setError(toDisplayError(requestError, 'Failed to load campaign links/creatives'));
      } finally {
        setLoadingCampaignAssets(false);
      }
    };

    loadCampaignAssets();
  }, [selectedCampaignId, selectedMerchantId, selectedCampaign]);

  useEffect(() => {
    const loadBulkSavedLinks = async () => {
      if (activeView !== 'bulk-links' || !selectedMerchantId || !selectedCampaignIds.length) {
        setBulkSavedLinks([]);
        return;
      }
      try {
        const response = await adminReportingAPI.listLinkTrustLinks({ merchant_id: selectedMerchantId, limit: 2000 });
        const links = response?.data?.links || [];
        const selectedSet = new Set(selectedCampaignIds.map((id) => Number(id)));
        setBulkSavedLinks(links.filter((row) => selectedSet.has(Number(row.campaign_id))));
      } catch (requestError) {
        setError(toDisplayError(requestError, 'Failed to load saved links for selected campaigns'));
      }
    };

    loadBulkSavedLinks();
  }, [activeView, selectedMerchantId, selectedCampaignIds]);

  const toggleSelection = (id, state, setState) => setState(state.includes(id) ? state.filter((item) => item !== id) : [...state, id]);

  const applyCampaignStatusBulkUpdate = async () => {
    if (!selectedMerchantId || !selectedCampaignIds.length) return setError('Select campaigns first.');
    try {
      setSavingCampaignStatuses(true);
      await adminReportingAPI.bulkUpdateLinkTrustCampaignStatuses({ merchantId: selectedMerchantId, campaignIds: selectedCampaignIds, ...campaignBulkEdit });
      await fetchWorkspace({ soft: true });
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Failed to update campaign statuses'));
    } finally {
      setSavingCampaignStatuses(false);
    }
  };

  const saveSelectedCampaignDetails = async (pushToLinkTrust) => {
    if (!selectedMerchantId || !selectedCampaign) return setError('Select a campaign first.');
    if (pushToLinkTrust && !credentials) return setError('Missing LinkTrust credentials in session. Add credentials in Automations first.');

    try {
      setSavingCampaignDetails(true);
      setError('');
      await adminReportingAPI.updateLinkTrustCampaignDetails(selectedCampaign.linktrust_campaign_id, {
        merchantId: selectedMerchantId,
        campaignName: campaignDetailsEdit.campaignName,
        category: campaignDetailsEdit.category,
        campaignType: campaignDetailsEdit.campaignType,
        defaultLandingPageUrl: campaignDetailsEdit.defaultLandingPageUrl,
        campaignStatus: campaignDetailsEdit.campaignStatus,
        pushToLinkTrust,
        credentials: pushToLinkTrust
          ? { username: credentials.username, password: credentials.password }
          : undefined
      });
      await fetchWorkspace({ soft: true });
    } catch (requestError) {
      setError(toDisplayError(requestError, pushToLinkTrust ? 'Failed to save and push campaign details' : 'Failed to save campaign details'));
    } finally {
      setSavingCampaignDetails(false);
    }
  };

  const pushSelectedCampaignStatusesToLinkTrust = async () => {
    const campaignIdsToPush = selectedCampaignIds.length
      ? selectedCampaignIds
      : (selectedCampaign ? [selectedCampaign.linktrust_campaign_id] : []);
    if (!selectedMerchantId || !campaignIdsToPush.length) return setError('Select campaigns first.');
    if (!credentials) return setError('Missing LinkTrust credentials in session. Add credentials in Automations first.');
    const statusToPush = campaignBulkEdit.desiredLinkTrustStatus || 'Testing';
    if (!linkTrustCampaignStatusOptions.includes(statusToPush)) {
      return setError('Choose a valid LinkTrust CampaignStatus before pushing.');
    }

    try {
      setPushingLinkTrustStatuses(true);
      setError('');
      await adminReportingAPI.pushLinkTrustCampaignStatuses({
        merchantId: selectedMerchantId,
        campaignIds: campaignIdsToPush,
        campaignStatus: statusToPush,
        credentials: {
          username: credentials.username,
          password: credentials.password
        }
      });
      await fetchWorkspace({ soft: true });
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Failed to push campaign statuses to LinkTrust'));
    } finally {
      setPushingLinkTrustStatuses(false);
    }
  };

  const backfillCampaignAdvertiserMappings = async () => {
    if (!selectedMerchantId) return setError('Select merchant first.');
    try {
      setSyncingCampaignAdvertisers(true);
      setError('');
      await adminReportingAPI.backfillLinkTrustCampaignAdvertisers({ merchantId: selectedMerchantId, force: true });
      await fetchWorkspace({ soft: true });
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Failed to backfill campaign advertiser mapping'));
    } finally {
      setSyncingCampaignAdvertisers(false);
    }
  };

  const addCreativeToSelectedCampaign = async () => {
    if (!selectedCampaign || !selectedMerchantId) return setError('Select a campaign first.');
    if (!credentials) return setError('Missing LinkTrust credentials in session. Add credentials in Automations first.');
    if (!creativeForm.creativeName.trim()) return setError('Creative name is required.');
    try {
      setAddingCreative(true);
      await adminReportingAPI.importLinkTrustCreatives({
        rows: [{
          campaignId: selectedCampaign.linktrust_campaign_id,
          name: creativeForm.creativeName,
          creativeType: Number.parseInt(creativeForm.creativeType, 10) || 1,
          adId: creativeForm.adId ? Number.parseInt(creativeForm.adId, 10) : undefined,
          content: creativeForm.content || undefined,
          sizeId: creativeForm.sizeId ? Number.parseInt(creativeForm.sizeId, 10) : undefined,
          enabled: creativeForm.enabled,
          customLandingPageUrl: creativeForm.destinationUrl || undefined,
          propageAdditionalData: creativeForm.propagateAdditionalData,
          fileOrImageUrl: creativeForm.imageUrl || undefined,
          affiliatePayoutType: creativeForm.affiliatePayoutType ? Number.parseInt(creativeForm.affiliatePayoutType, 10) : undefined,
          affiliatePayout: creativeForm.affiliatePayout ? Number.parseFloat(creativeForm.affiliatePayout) : undefined,
          advertiserRevenue: creativeForm.advertiserRevenue ? Number.parseFloat(creativeForm.advertiserRevenue) : undefined,
          enableForAllAffiliates: creativeForm.enableForAllAffiliates,
          affiliateGroupId: creativeForm.affiliateGroupId ? Number.parseInt(creativeForm.affiliateGroupId, 10) : undefined,
          showTrackingLinkInListing: creativeForm.showTrackingLinkInListing,
          displaySubjectInAC: creativeForm.displaySubjectInAC,
          downloadOnlyForAllAffiliates: creativeForm.downloadOnlyForAllAffiliates,
          groupId: creativeForm.groupId ? Number.parseInt(creativeForm.groupId, 10) : undefined,
          subject: creativeForm.subject || undefined,
          affiliates: creativeForm.affiliates || undefined
        }],
        credentials: { username: credentials.username, password: credentials.password, merchantId: selectedMerchantId },
        options: { dryRun: false, stopOnError: true, campaignIdOverride: selectedCampaign.linktrust_campaign_id }
      });
      setCreativeForm((state) => ({ ...state, creativeName: '', adId: '', content: '', sizeId: '', destinationUrl: '', imageUrl: '', affiliatePayout: '', advertiserRevenue: '', subject: '', affiliates: '' }));
      await fetchWorkspace({ soft: true });
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Failed to assign creative to campaign'));
    } finally {
      setAddingCreative(false);
    }
  };

  const runLinkGeneration = async (previewOnly) => {
    const campaignIdsForLinks = selectedCampaignIds.length
      ? selectedCampaignIds
      : (selectedCampaign ? [selectedCampaign.linktrust_campaign_id] : []);
    if (!selectedMerchantId || !campaignIdsForLinks.length || !selectedAffiliateIds.length) return setError('Select campaign(s) and affiliate(s) first.');
    try {
      setGeneratingLinks(true);
      const response = await adminReportingAPI.generateLinkTrustLinks({
        merchantId: selectedMerchantId,
        campaignIds: campaignIdsForLinks,
        affiliateIds: selectedAffiliateIds,
        options: {
          includeBaseLinks: bulkLinkOptions.includeBaseLinks,
          includeAdIds: bulkLinkOptions.includeAdIds,
          previewOnly
        }
      });
      setLinkPreview(response?.data?.links || []);
      if (!previewOnly) {
        await fetchWorkspace({ soft: true });
        if (activeView === 'bulk-links') {
          const linksResponse = await adminReportingAPI.listLinkTrustLinks({ merchant_id: selectedMerchantId, limit: 2000 });
          const links = linksResponse?.data?.links || [];
          const selectedSet = new Set(campaignIdsForLinks.map((id) => Number(id)));
          setBulkSavedLinks(links.filter((row) => selectedSet.has(Number(row.campaign_id))));
        }
      }
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Failed to generate links'));
    } finally {
      setGeneratingLinks(false);
    }
  };

  const rebuildAdvertisersFromCampaigns = async () => {
    if (!selectedMerchantId) return setError('Select merchant first.');
    try {
      setRebuildingAdvertisers(true);
      await adminReportingAPI.rebuildLinkTrustAdvertisersFromCampaigns({ merchantId: selectedMerchantId, clearExisting: true });
      await fetchWorkspace({ soft: true });
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Failed to rebuild advertisers from campaigns'));
    } finally {
      setRebuildingAdvertisers(false);
    }
  };

  const selectAllVisibleCampaigns = () => {
    const visibleIds = filteredRows.map((row) => toCampaignId(row)).filter((value) => Number.isFinite(value) && value > 0);
    setSelectedCampaignIds(Array.from(new Set(visibleIds)));
  };

  const clearVisibleCampaignSelection = () => {
    const visible = new Set(filteredRows.map((row) => toCampaignId(row)).filter((value) => Number.isFinite(value) && value > 0));
    setSelectedCampaignIds((state) => state.filter((id) => !visible.has(Number(id))));
  };

  const bulkDeleteSelectedCampaigns = async () => {
    if (!selectedMerchantId || !selectedCampaignIds.length) {
      setError('Select campaign(s) first.');
      return;
    }

    const confirmed = window.confirm(`Delete ${selectedCampaignIds.length} selected campaign(s) and related local records? This is intended for bad imports before re-sync/re-import.`);
    if (!confirmed) return;

    const promptValue = window.prompt('Type DELETE to confirm campaign deletion');
    if (promptValue !== 'DELETE') {
      setError('Deletion cancelled. Confirmation phrase did not match DELETE.');
      return;
    }

    try {
      setDeletingCampaigns(true);
      setError('');
      await adminReportingAPI.bulkDeleteLinkTrustCampaigns({
        merchantId: selectedMerchantId,
        campaignIds: selectedCampaignIds,
        confirmPhrase: 'DELETE'
      });
      setSelectedCampaignIds([]);
      setSelectedCampaignId(null);
      await fetchWorkspace({ soft: true });
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Failed to delete selected campaigns'));
    } finally {
      setDeletingCampaigns(false);
    }
  };

  const downloadCsv = (filename, rows) => {
    if (!rows.length) return;
    const headers = Object.keys(rows[0]);
    const escape = (value) => {
      const text = String(value ?? '');
      if (text.includes('"') || text.includes(',') || text.includes('\n')) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    };
    const csv = [headers.join(','), ...rows.map((row) => headers.map((header) => escape(row[header])).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadSelectedCampaignCreatives = async () => {
    if (!selectedMerchantId || !selectedCampaignIds.length) return setError('Select campaigns first.');
    try {
      const response = await adminReportingAPI.listLinkTrustCreatives({ merchant_id: selectedMerchantId, limit: 4000 });
      const allCreatives = response?.data?.creatives || [];
      const selectedSet = new Set(selectedCampaignIds.map((id) => Number(id)));
      const scoped = allCreatives.filter((row) => selectedSet.has(Number(row.campaign_id)));
      setBulkCampaignCreatives(scoped);
      downloadCsv(`campaign-creatives-merchant-${selectedMerchantId}.csv`, scoped.map((row) => ({
        campaign_id: row.campaign_id,
        ad_id: row.ad_id,
        creative_name: row.creative_name,
        creative_type: row.creative_type,
        import_status: row.import_status,
        created_at: row.created_at
      })));
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Failed to download selected campaign creatives'));
    }
  };

  const downloadCurrentCampaignCreatives = () => {
    if (!selectedCampaign || !campaignCreatives.length) return;
    downloadCsv(`campaign-${selectedCampaign.linktrust_campaign_id}-creatives.csv`, campaignCreatives.map((row) => ({
      campaign_id: row.campaign_id,
      ad_id: row.ad_id,
      creative_name: row.creative_name,
      creative_type: row.creative_type,
      import_status: row.import_status,
      created_at: row.created_at
    })));
  };

  const saveAdvertiserManagement = async () => {
    if (!selectedMerchantId) return;
    try {
      setSavingAdvertiser(true);
      await adminReportingAPI.updateLinkTrustAdvertiserManagement(selectedMerchantId, advertiserEdit);
      await fetchWorkspace({ soft: true });
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Failed to save advertiser settings'));
    } finally {
      setSavingAdvertiser(false);
    }
  };

  const saveAffiliateManagement = async () => {
    if (!selectedMerchantId || !selectedAffiliate) return;
    try {
      setSavingAffiliate(true);
      await adminReportingAPI.updateLinkTrustAffiliateManagement(selectedMerchantId, selectedAffiliate.affiliate_id, affiliateEdit);
      await fetchWorkspace({ soft: true });
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Failed to save affiliate settings'));
    } finally {
      setSavingAffiliate(false);
    }
  };

  const applySelectedProfileToManagement = async () => {
    if (!selectedAutomationProfileId) {
      setError('Select an automation profile first.');
      return;
    }
    try {
      setError('');
      await applyAutomationProfileContext(selectedAutomationProfileId);
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Failed to apply automation profile context'));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-3xl font-bold text-slate-100">Management</h2>
          <p className="text-slate-400 mt-1">Operational workspace with drill-in views for campaigns, advertisers, and affiliates.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/automations" className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-lg text-sm">Open Automations</Link>
          <button onClick={() => fetchWorkspace({ soft: true })} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-lg inline-flex items-center text-sm"><RefreshCcw className="w-4 h-4 mr-2" />{refreshing ? 'Refreshing...' : 'Refresh'}</button>
        </div>
      </div>

      <div className="admin-glass rounded-xl border border-slate-700/50 p-3 grid grid-cols-1 md:grid-cols-6 gap-3">
        <select value={selectedAutomationProfileId} onChange={(event) => setSelectedAutomationProfileId(event.target.value)} className="md:col-span-2 px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100" >
          <option value="">Select automation profile context</option>
          {automationProfiles.map((profile) => (
            <option key={`management-automation-profile-${profile.id}`} value={String(profile.id)}>
              {profile.client_name} · {profile.profile_name}
            </option>
          ))}
        </select>
        <button type="button" onClick={applySelectedProfileToManagement} className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-lg text-sm">Apply Profile Context</button>
        <input list="management-merchant-options" value={merchantId} onChange={(event) => setMerchantId(String(event.target.value || '').replace(/[^0-9]/g, ''))} className="md:col-span-2 px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100" placeholder="Merchant ID" />
        <datalist id="management-merchant-options">{merchantOptions.map((option) => <option key={`merchant-${option.merchantId}`} value={String(option.merchantId)} label={`${option.merchantName} (${option.merchantId})`} />)}</datalist>
        {activeView === 'campaigns' || activeView === 'bulk-links' ? (
          <select value={campaignAdvertiserFilter} onChange={(event) => setCampaignAdvertiserFilter(event.target.value)} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100">
            <option value="all">All advertisers</option>
            {campaignAdvertiserOptions.map((option) => <option key={`header-${option.value}`} value={option.value}>{option.label}</option>)}
          </select>
        ) : (
          <div className="px-3 py-2 rounded-lg border border-slate-700 bg-slate-900/40 text-xs text-slate-500 flex items-center">Advertiser filter available in Campaigns view</div>
        )}
        <input value={search} onChange={(event) => setSearch(event.target.value)} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100" placeholder="Search current view" />
        <div className="px-3 py-2 rounded-lg border border-slate-700 bg-slate-900/40 text-xs text-slate-400 flex items-center">Campaigns: {campaignRows.length} · Advertisers: {advertiserCountForHeader} · Affiliates: {(merchantSettings?.management?.affiliates || affiliateRows).length}</div>
      </div>

      {activeProfileContext?.profileId && (
        <div className="admin-glass rounded-xl border border-emerald-700/30 bg-emerald-900/10 p-3 text-xs text-emerald-200">
          Management context: {activeProfileContext.clientName || 'Client'} · {activeProfileContext.profileName || `Profile ${activeProfileContext.profileId}`}
          {activeProfileContext.merchantId ? ` · Merchant ${activeProfileContext.merchantId}` : ''}
          {activeProfileContext.username ? ` · ${activeProfileContext.username}` : ''}
        </div>
      )}

      {error && <div className="rounded-lg border border-red-700/40 bg-red-900/20 p-3 text-sm text-red-200">{error}</div>}
      {loading ? <div className="admin-glass rounded-xl border border-slate-700/50 p-8 text-slate-400">Loading management...</div> : (
        <div className="grid grid-cols-1 xl:grid-cols-[220px_1fr_1fr] gap-4">
          <aside className="admin-glass rounded-xl border border-slate-700/50 p-2">
            {views.map((view) => <button key={view.id} onClick={() => setActiveView(view.id)} className={`w-full text-left px-3 py-2 rounded-lg text-sm mb-1 ${activeView === view.id ? 'bg-red-700/20 border border-red-600/40 text-red-200' : 'text-slate-300 hover:bg-slate-800/50'}`}>{view.label}</button>)}
          </aside>

          <section className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
            <div className="p-3 border-b border-slate-700/50 text-sm text-slate-200">{views.find((view) => view.id === activeView)?.label}</div>
            <div className="max-h-[680px] overflow-auto p-3">
              {activeView === 'campaigns' && (
                <>
                  <div className="mb-3 p-2 rounded border border-slate-700 bg-slate-900/40 space-y-2">
                    <div className="flex gap-2">
                      <button onClick={selectAllVisibleCampaigns} className="px-2 py-1 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded text-xs">Select all shown</button>
                      <button onClick={clearVisibleCampaignSelection} className="px-2 py-1 bg-slate-900 hover:bg-slate-800 text-slate-200 rounded text-xs">Clear shown</button>
                      <button onClick={() => setActiveView('bulk-links')} disabled={!selectedCampaignIds.length} className="px-2 py-1 bg-red-700 hover:bg-red-600 disabled:opacity-40 text-white rounded text-xs">Bulk Links ({selectedCampaignIds.length})</button>
                      <button onClick={() => setShowCampaignMassActions((value) => !value)} className="px-2 py-1 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded text-xs inline-flex items-center"><Wrench className="w-3 h-3 mr-1" />Mass Actions</button>
                    </div>
                    {showCampaignMassActions && (
                      <div className="pt-2 border-t border-slate-700/60">
                        <button onClick={bulkDeleteSelectedCampaigns} disabled={deletingCampaigns || !selectedCampaignIds.length} className="px-3 py-1.5 bg-red-800 hover:bg-red-700 disabled:opacity-40 text-red-100 rounded text-xs inline-flex items-center"><Trash2 className="w-3 h-3 mr-1" />{deletingCampaigns ? 'Deleting...' : `Delete Selected (${selectedCampaignIds.length})`}</button>
                      </div>
                    )}
                  </div>
                  {filteredRows.map((row) => (
                    <div key={`camp-${row.linktrust_campaign_id}`} className="grid grid-cols-[24px_1fr_auto] gap-2 items-center p-2 rounded border border-slate-700 bg-slate-900/40 mb-2">
                      <input type="checkbox" checked={selectedCampaignIds.includes(toCampaignId(row))} onChange={() => toggleSelection(toCampaignId(row), selectedCampaignIds, setSelectedCampaignIds)} className="rounded bg-slate-700 border-slate-600" />
                      <button type="button" onClick={() => setSelectedCampaignId(row.linktrust_campaign_id)} className="text-left text-sm text-slate-100">
                        {row.campaign_name} ({row.linktrust_campaign_id})
                        <span className="block text-xs text-slate-500">{row.advertiser_name || 'No advertiser mapped'} · {row.workspace_status || 'backlog'} · LT: {row.campaign_status || 'Unknown'}</span>
                      </button>
                      <span className="text-xs text-slate-500">{row.category || '-'}</span>
                    </div>
                  ))}
                </>
              )}

              {activeView === 'bulk-links' && (
                <div className="space-y-3">
                  <div className="rounded border border-slate-700 bg-slate-900/40 p-3">
                    <p className="text-xs text-slate-300 font-medium">Selected Campaigns ({selectedCampaignIds.length})</p>
                    <div className="max-h-48 overflow-auto mt-2 space-y-1">
                      {campaignRows
                        .filter((row) => selectedCampaignIds.includes(toCampaignId(row)))
                        .map((row) => (
                          <p key={`bulk-selected-campaign-${row.linktrust_campaign_id}`} className="text-xs text-slate-200">
                            {row.campaign_name} ({row.linktrust_campaign_id})
                            <span className="text-slate-500"> · {row.advertiser_name || 'No advertiser mapped'}</span>
                          </p>
                        ))}
                    </div>
                  </div>
                  <div className="rounded border border-slate-700 bg-slate-900/40 p-3">
                    <p className="text-xs text-slate-300 font-medium">Saved Links For Selected Campaigns ({bulkSavedLinks.length})</p>
                    <div className="max-h-64 overflow-auto mt-2 space-y-1">
                      {renderLinkRows(bulkSavedLinks.slice(0, 400), { useSavedUrl: true, useSavedAdId: true, keyPrefix: 'bulk-saved' })}
                    </div>
                  </div>
                </div>
              )}

              {activeView === 'advertisers' && filteredRows.map((row, idx) => {
                const key = `${row.merchant_id}:${row.advertiser_id || 'unknown'}:${(row.company_name || row.advertiser_name || '').toLowerCase()}`;
                return (
                  <button key={`adv-${idx}`} type="button" onClick={() => setSelectedAdvertiserKey(key)} className="w-full text-left p-2 rounded border border-slate-700 bg-slate-900/40 mb-2">
                    <p className="text-sm text-slate-100">{row.company_name || row.advertiser_name || `Advertiser ${row.advertiser_id || '-'}`}</p>
                    <p className="text-xs text-slate-500">ID: {row.advertiser_id || '-'} · {row.email || 'No email'}</p>
                  </button>
                );
              })}

              {activeView === 'affiliates' && filteredRows.map((row) => (
                <div key={`aff-${row.affiliate_id}`} className="grid grid-cols-[24px_1fr] gap-2 items-start p-2 rounded border border-slate-700 bg-slate-900/40 mb-2">
                  <input type="checkbox" checked={selectedAffiliateIds.includes(row.affiliate_id)} onChange={() => toggleSelection(row.affiliate_id, selectedAffiliateIds, setSelectedAffiliateIds)} className="rounded bg-slate-700 border-slate-600 mt-1" />
                  <button type="button" onClick={() => { setSelectedAffiliateId(row.affiliate_id); setAffiliateEdit({ partnerStatus: row.partner_status || 'active', ownerName: row.owner_name || '', tags: row.tags || '', notes: row.notes || '', syncToLinkTrust: row.sync_to_linktrust === true, queueLinkTrustSync: false }); }} className="text-left">
                    <p className="text-sm text-slate-100">{row.affiliate_name || row.affiliate_id} ({row.affiliate_id})</p>
                    <p className="text-xs text-slate-500">{row.email || 'No email'} · {row.partner_status || 'active'}</p>
                  </button>
                </div>
              ))}

              {activeView === 'queue' && (merchantSettings?.outboundQueue || []).map((row) => (
                <div key={`queue-${row.id}`} className="p-2 rounded border border-slate-700 bg-slate-900/40 mb-2 text-xs text-slate-300">
                  <p>{row.entity_type}:{row.entity_id} · {row.operation}</p>
                  <p className="text-slate-500">Status: {row.queue_status}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
            <div className="p-3 border-b border-slate-700/50 text-sm text-slate-200">Detail</div>
            <div className="p-3 max-h-[680px] overflow-auto space-y-3">
              {activeView === 'campaigns' && (
                <>
                  <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 space-y-2">
                    <h4 className="text-slate-100 text-sm font-semibold">Bulk Campaign Status</h4>
                    <select value={campaignBulkEdit.workspaceStatus} onChange={(event) => setCampaignBulkEdit((state) => ({ ...state, workspaceStatus: event.target.value }))} className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm">{campaignStatusOptions.map((status) => <option key={status} value={status}>{status}</option>)}</select>
                    <select value={campaignBulkEdit.desiredLinkTrustStatus} onChange={(event) => setCampaignBulkEdit((state) => ({ ...state, desiredLinkTrustStatus: event.target.value }))} className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm">{linkTrustCampaignStatusOptions.map((status) => <option key={status} value={status}>{status}</option>)}</select>
                    <input value={campaignBulkEdit.ownerName} onChange={(event) => setCampaignBulkEdit((state) => ({ ...state, ownerName: event.target.value }))} placeholder="Owner" className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                    <input value={campaignBulkEdit.notes} onChange={(event) => setCampaignBulkEdit((state) => ({ ...state, notes: event.target.value }))} placeholder="Notes" className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                    <label className="inline-flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={campaignBulkEdit.queueLinkTrustSync} onChange={(event) => setCampaignBulkEdit((state) => ({ ...state, queueLinkTrustSync: event.target.checked }))} className="rounded bg-slate-700 border-slate-600" />Queue outbound LinkTrust update</label>
                    <div className="flex gap-2">
                      <button onClick={applyCampaignStatusBulkUpdate} disabled={savingCampaignStatuses} className="px-4 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-60 text-white rounded text-sm">{savingCampaignStatuses ? 'Saving...' : `Save Local (${selectedCampaignIds.length})`}</button>
                      <button onClick={pushSelectedCampaignStatusesToLinkTrust} disabled={pushingLinkTrustStatuses} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-60 text-slate-100 rounded text-sm">{pushingLinkTrustStatuses ? 'Pushing...' : 'Push To LinkTrust'}</button>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={backfillCampaignAdvertiserMappings} disabled={syncingCampaignAdvertisers} className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-slate-200 rounded text-xs">{syncingCampaignAdvertisers ? 'Repairing...' : 'Repair Campaign Advertiser Mapping'}</button>
                    </div>
                  </div>

                  {!selectedCampaign ? <p className="text-sm text-slate-500">Select a campaign to manage it directly.</p> : (
                    <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 space-y-2">
                      <h4 className="text-slate-100 text-sm font-semibold">Campaign Detail</h4>
                      <p className="text-sm text-slate-200">{selectedCampaign.campaign_name} ({selectedCampaign.linktrust_campaign_id})</p>
                      <p className="text-xs text-slate-500">Advertiser: {selectedCampaign.advertiser_name || '-'} · Workspace: {selectedCampaign.workspace_status || 'backlog'} · LinkTrust: {selectedCampaign.campaign_status || 'Unknown'}</p>

                      <div className="pt-2 border-t border-slate-700/60 space-y-2">
                        <p className="text-xs text-slate-300 font-medium">Synced Campaign Details (Editable)</p>
                        <input value={campaignDetailsEdit.campaignName} onChange={(event) => setCampaignDetailsEdit((state) => ({ ...state, campaignName: event.target.value }))} placeholder="Campaign Name" className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                        <input value={campaignDetailsEdit.category} onChange={(event) => setCampaignDetailsEdit((state) => ({ ...state, category: event.target.value }))} placeholder="Category" className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                        <div className="grid grid-cols-2 gap-2">
                          <select value={campaignDetailsEdit.campaignType} onChange={(event) => setCampaignDetailsEdit((state) => ({ ...state, campaignType: event.target.value }))} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm">
                            <option value="CPA">CPA</option>
                            <option value="CPL">CPL</option>
                            <option value="CPC">CPC</option>
                          </select>
                          <select value={campaignDetailsEdit.campaignStatus} onChange={(event) => setCampaignDetailsEdit((state) => ({ ...state, campaignStatus: event.target.value }))} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm">
                            {linkTrustCampaignStatusOptions.map((status) => <option key={`detail-status-${status}`} value={status}>{status}</option>)}
                          </select>
                        </div>
                        <input value={campaignDetailsEdit.defaultLandingPageUrl} onChange={(event) => setCampaignDetailsEdit((state) => ({ ...state, defaultLandingPageUrl: event.target.value }))} placeholder="DefaultLandingPageURL" className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                        <div className="flex gap-2">
                          <button onClick={() => saveSelectedCampaignDetails(false)} disabled={savingCampaignDetails} className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded text-xs">{savingCampaignDetails ? 'Saving...' : 'Save Local'}</button>
                          <button onClick={() => saveSelectedCampaignDetails(true)} disabled={savingCampaignDetails} className="px-3 py-1.5 bg-red-700 hover:bg-red-600 text-white rounded text-xs">Save + Push To LinkTrust</button>
                        </div>
                      </div>

                      <div className="pt-2 border-t border-slate-700/60 space-y-2">
                        <p className="text-xs text-slate-300 font-medium inline-flex items-center"><Link2 className="w-3 h-3 mr-1" />Links For This Campaign</p>
                        <p className="text-[11px] text-slate-500">Select affiliates here, then preview/generate links for this campaign.</p>
                        <div className="max-h-28 overflow-auto rounded border border-slate-700 bg-slate-950/40 p-2 space-y-1">
                          {affiliateRows.slice(0, 120).map((affiliate) => (
                            <label key={`bulk-aff-${affiliate.affiliate_id}`} className="flex items-center gap-2 text-xs text-slate-200">
                              <input type="checkbox" checked={selectedAffiliateIds.includes(affiliate.affiliate_id)} onChange={() => toggleSelection(affiliate.affiliate_id, selectedAffiliateIds, setSelectedAffiliateIds)} className="rounded bg-slate-700 border-slate-600" />
                              <span>{affiliate.affiliate_name || affiliate.affiliate_id} ({affiliate.affiliate_id})</span>
                            </label>
                          ))}
                        </div>
                        <div className="flex gap-2"><button onClick={() => runLinkGeneration(true)} disabled={generatingLinks} className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded text-xs">Preview</button><button onClick={() => runLinkGeneration(false)} disabled={generatingLinks} className="px-3 py-1.5 bg-red-700 hover:bg-red-600 text-white rounded text-xs">Generate + Save</button></div>
                        <p className="text-[11px] text-slate-500">Saved links for this campaign: {campaignLinks.length}</p>
                        <div className="max-h-24 overflow-auto rounded border border-slate-700 bg-slate-950/40 p-2">
                          {loadingCampaignAssets ? <p className="text-[11px] text-slate-500">Loading links...</p> : campaignLinks.slice(0, 30).map((link) => <p key={`campaign-link-${link.id}`} className="text-[11px] text-slate-300 break-all mb-1">{link.generated_url}</p>)}
                        </div>
                      </div>

                      <div className="pt-2 border-t border-slate-700/60 space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-slate-300 font-medium">Current Creatives + Assign AD</p>
                          <div className="flex gap-2">
                            <button onClick={downloadCurrentCampaignCreatives} disabled={!campaignCreatives.length} className="text-[11px] px-2 py-1 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-100 rounded">Download CSV</button>
                            <Link to="/automations" className="text-[11px] text-red-300 hover:text-red-200 inline-flex items-center">Creative upload automation <ExternalLink className="w-3 h-3 ml-1" /></Link>
                          </div>
                        </div>
                        <div className="max-h-24 overflow-auto rounded border border-slate-700 bg-slate-950/40 p-2">
                          {loadingCampaignAssets ? <p className="text-[11px] text-slate-500">Loading creatives...</p> : campaignCreatives.length === 0 ? <p className="text-[11px] text-slate-500">No creatives stored for this campaign.</p> : campaignCreatives.map((creative, index) => <p key={`campaign-creative-${index}`} className="text-[11px] text-slate-300 mb-1">{creative.creative_name} · ADID: {creative.ad_id || '-'} · Type: {creative.creative_type || '-'}</p>)}
                        </div>
                      <h5 className="text-xs text-slate-400 mt-2 inline-flex items-center"><Sparkles className="w-3 h-3 mr-1" />Assign Creative To This Campaign</h5>
                      <input value={creativeForm.creativeName} onChange={(event) => setCreativeForm((state) => ({ ...state, creativeName: event.target.value }))} placeholder="Creative name" className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                      <div className="grid grid-cols-2 gap-2">
                        <input value={creativeForm.creativeType} onChange={(event) => setCreativeForm((state) => ({ ...state, creativeType: event.target.value.replace(/[^0-9]/g, '') }))} placeholder="Creative type" className="px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                        <input value={creativeForm.adId} onChange={(event) => setCreativeForm((state) => ({ ...state, adId: event.target.value.replace(/[^0-9]/g, '') }))} placeholder="Optional ADID" className="px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                      </div>
                      <textarea value={creativeForm.content} onChange={(event) => setCreativeForm((state) => ({ ...state, content: event.target.value }))} placeholder="Content/HTML" className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm min-h-[72px]" />
                      <div className="grid grid-cols-2 gap-2">
                        <input value={creativeForm.sizeId} onChange={(event) => setCreativeForm((state) => ({ ...state, sizeId: event.target.value.replace(/[^0-9]/g, '') }))} placeholder="Size ID" className="px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                        <input value={creativeForm.subject} onChange={(event) => setCreativeForm((state) => ({ ...state, subject: event.target.value }))} placeholder="Subject" className="px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                      </div>
                      <input value={creativeForm.destinationUrl} onChange={(event) => setCreativeForm((state) => ({ ...state, destinationUrl: event.target.value }))} placeholder="Destination URL" className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                      <input value={creativeForm.imageUrl} onChange={(event) => setCreativeForm((state) => ({ ...state, imageUrl: event.target.value }))} placeholder="Asset URL" className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                      <div className="grid grid-cols-2 gap-2">
                        <input value={creativeForm.affiliatePayoutType} onChange={(event) => setCreativeForm((state) => ({ ...state, affiliatePayoutType: event.target.value.replace(/[^0-9]/g, '') }))} placeholder="Affiliate Payout Type" className="px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                        <input value={creativeForm.affiliatePayout} onChange={(event) => setCreativeForm((state) => ({ ...state, affiliatePayout: event.target.value }))} placeholder="Affiliate Payout" className="px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input value={creativeForm.advertiserRevenue} onChange={(event) => setCreativeForm((state) => ({ ...state, advertiserRevenue: event.target.value }))} placeholder="Advertiser Revenue" className="px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                        <input value={creativeForm.groupId} onChange={(event) => setCreativeForm((state) => ({ ...state, groupId: event.target.value.replace(/[^0-9]/g, '') }))} placeholder="Group ID" className="px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input value={creativeForm.affiliateGroupId} onChange={(event) => setCreativeForm((state) => ({ ...state, affiliateGroupId: event.target.value.replace(/[^0-9]/g, '') }))} placeholder="Affiliate Group ID" className="px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                        <input value={creativeForm.affiliates} onChange={(event) => setCreativeForm((state) => ({ ...state, affiliates: event.target.value }))} placeholder="Affiliates (comma ids)" className="px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs text-slate-300">
                        <label className="inline-flex items-center gap-2"><input type="checkbox" checked={creativeForm.enabled} onChange={(event) => setCreativeForm((state) => ({ ...state, enabled: event.target.checked }))} className="rounded bg-slate-700 border-slate-600" />Enabled</label>
                        <label className="inline-flex items-center gap-2"><input type="checkbox" checked={creativeForm.propagateAdditionalData} onChange={(event) => setCreativeForm((state) => ({ ...state, propagateAdditionalData: event.target.checked }))} className="rounded bg-slate-700 border-slate-600" />PropageAdditionalData</label>
                        <label className="inline-flex items-center gap-2"><input type="checkbox" checked={creativeForm.enableForAllAffiliates} onChange={(event) => setCreativeForm((state) => ({ ...state, enableForAllAffiliates: event.target.checked }))} className="rounded bg-slate-700 border-slate-600" />EnableForAllAffiliates</label>
                        <label className="inline-flex items-center gap-2"><input type="checkbox" checked={creativeForm.showTrackingLinkInListing} onChange={(event) => setCreativeForm((state) => ({ ...state, showTrackingLinkInListing: event.target.checked }))} className="rounded bg-slate-700 border-slate-600" />ShowTrackingLinkInListing</label>
                        <label className="inline-flex items-center gap-2"><input type="checkbox" checked={creativeForm.displaySubjectInAC} onChange={(event) => setCreativeForm((state) => ({ ...state, displaySubjectInAC: event.target.checked }))} className="rounded bg-slate-700 border-slate-600" />DisplaySubjectInAC</label>
                        <label className="inline-flex items-center gap-2"><input type="checkbox" checked={creativeForm.downloadOnlyForAllAffiliates} onChange={(event) => setCreativeForm((state) => ({ ...state, downloadOnlyForAllAffiliates: event.target.checked }))} className="rounded bg-slate-700 border-slate-600" />DownloadOnlyForAllAffiliates</label>
                      </div>
                      <button onClick={addCreativeToSelectedCampaign} disabled={addingCreative} className="px-4 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-60 text-white rounded text-sm">{addingCreative ? 'Adding...' : 'Add Creative To Campaign'}</button>
                    </div>
                    </div>
                  )}
                </>
              )}

              {activeView === 'bulk-links' && (
                <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 space-y-3">
                  <h4 className="text-slate-100 text-sm font-semibold">Bulk Links Workspace</h4>
                  <p className="text-xs text-slate-500">Uses your current campaign selection from Campaigns view.</p>
                  <p className="text-xs text-slate-400">Selected campaigns: {selectedCampaignIds.length}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="inline-flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={showLinkCampaignContext} onChange={(event) => setShowLinkCampaignContext(event.target.checked)} className="rounded bg-slate-700 border-slate-600" />Show campaign name</label>
                    <label className="inline-flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={showLinkAdContext} onChange={(event) => setShowLinkAdContext(event.target.checked)} className="rounded bg-slate-700 border-slate-600" />Show ad/creative info</label>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="inline-flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={bulkLinkOptions.includeBaseLinks} onChange={(event) => setBulkLinkOptions((state) => ({ ...state, includeBaseLinks: event.target.checked }))} className="rounded bg-slate-700 border-slate-600" />Base campaign links</label>
                    <label className="inline-flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={bulkLinkOptions.includeAdIds} onChange={(event) => setBulkLinkOptions((state) => ({ ...state, includeAdIds: event.target.checked }))} className="rounded bg-slate-700 border-slate-600" />Campaign + ad links</label>
                  </div>
                  <div className="max-h-36 overflow-auto rounded border border-slate-700 bg-slate-950/40 p-2 space-y-1">
                    {affiliateRows.slice(0, 300).map((affiliate) => (
                      <label key={`bulk-links-aff-${affiliate.affiliate_id}`} className="flex items-center gap-2 text-xs text-slate-200">
                        <input type="checkbox" checked={selectedAffiliateIds.includes(affiliate.affiliate_id)} onChange={() => toggleSelection(affiliate.affiliate_id, selectedAffiliateIds, setSelectedAffiliateIds)} className="rounded bg-slate-700 border-slate-600" />
                        <span>{affiliate.affiliate_name || affiliate.affiliate_id} ({affiliate.affiliate_id})</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => runLinkGeneration(true)} disabled={generatingLinks} className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded text-xs">Preview Links</button>
                    <button onClick={() => runLinkGeneration(false)} disabled={generatingLinks} className="px-3 py-1.5 bg-red-700 hover:bg-red-600 text-white rounded text-xs">Generate + Save Links</button>
                  </div>
                  <div className="pt-2 border-t border-slate-700/60 space-y-2">
                    <p className="text-xs text-slate-300 font-medium">Bulk Creative Download</p>
                    <button onClick={downloadSelectedCampaignCreatives} disabled={!selectedCampaignIds.length} className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-100 rounded text-xs">Download Creatives CSV For Selected Campaigns</button>
                    <p className="text-[11px] text-slate-500">Last export creatives: {bulkCampaignCreatives.length}</p>
                  </div>
                  <div className="rounded border border-slate-700 bg-slate-950/40 p-2 max-h-32 overflow-auto">
                    {renderLinkRows(linkPreview.slice(0, 80), { keyPrefix: 'bulk-preview' })}
                  </div>
                </div>
              )}

              {activeView === 'advertisers' && (
                <>
                  <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3">
                    <button onClick={rebuildAdvertisersFromCampaigns} disabled={rebuildingAdvertisers} className="px-4 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-60 text-white rounded text-sm inline-flex items-center"><Database className="w-4 h-4 mr-2" />{rebuildingAdvertisers ? 'Rebuilding...' : 'Clear + Rebuild From Campaigns'}</button>
                    <p className="text-xs text-slate-500 mt-2">Uses advertiser ID/name from campaign sync payloads. Existing directory rows are cleared first.</p>
                  </div>
                  {!selectedAdvertiser ? <p className="text-sm text-slate-500">Select an advertiser.</p> : (
                    <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 space-y-2">
                      <p className="text-sm text-slate-100">{selectedAdvertiser.company_name || selectedAdvertiser.advertiser_name}</p>
                      <p className="text-xs text-slate-500">Advertiser ID: {selectedAdvertiser.advertiser_id || '-'} · Source: {selectedAdvertiser.source || '-'}</p>
                      <input value={advertiserEdit.accountStatus} onChange={(event) => setAdvertiserEdit((state) => ({ ...state, accountStatus: event.target.value }))} placeholder="Account status" className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                      <input value={advertiserEdit.ownerName} onChange={(event) => setAdvertiserEdit((state) => ({ ...state, ownerName: event.target.value }))} placeholder="Owner" className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                      <input value={advertiserEdit.tags} onChange={(event) => setAdvertiserEdit((state) => ({ ...state, tags: event.target.value }))} placeholder="Tags" className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                      <input value={advertiserEdit.notes} onChange={(event) => setAdvertiserEdit((state) => ({ ...state, notes: event.target.value }))} placeholder="Notes" className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                      <label className="inline-flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={advertiserEdit.syncToLinkTrust} onChange={(event) => setAdvertiserEdit((state) => ({ ...state, syncToLinkTrust: event.target.checked }))} className="rounded bg-slate-700 border-slate-600" />Enable sync intent</label>
                      <label className="inline-flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={advertiserEdit.queueLinkTrustSync} onChange={(event) => setAdvertiserEdit((state) => ({ ...state, queueLinkTrustSync: event.target.checked }))} className="rounded bg-slate-700 border-slate-600" />Queue outbound update</label>
                      <button onClick={saveAdvertiserManagement} disabled={savingAdvertiser} className="px-4 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-60 text-white rounded text-sm">{savingAdvertiser ? 'Saving...' : 'Save Advertiser Settings'}</button>
                    </div>
                  )}
                </>
              )}

              {activeView === 'affiliates' && (
                !selectedAffiliate ? <p className="text-sm text-slate-500">Select an affiliate.</p> : (
                  <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 space-y-2">
                    <h4 className="text-slate-100 text-sm font-semibold">Affiliate Detail</h4>
                    <p className="text-sm text-slate-200">{selectedAffiliate.affiliate_name || selectedAffiliate.affiliate_id} ({selectedAffiliate.affiliate_id})</p>
                    <input value={affiliateEdit.partnerStatus} onChange={(event) => setAffiliateEdit((state) => ({ ...state, partnerStatus: event.target.value }))} placeholder="Partner status" className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                    <input value={affiliateEdit.ownerName} onChange={(event) => setAffiliateEdit((state) => ({ ...state, ownerName: event.target.value }))} placeholder="Owner" className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                    <input value={affiliateEdit.tags} onChange={(event) => setAffiliateEdit((state) => ({ ...state, tags: event.target.value }))} placeholder="Tags" className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                    <input value={affiliateEdit.notes} onChange={(event) => setAffiliateEdit((state) => ({ ...state, notes: event.target.value }))} placeholder="Notes" className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-100 text-sm" />
                    <label className="inline-flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={affiliateEdit.syncToLinkTrust} onChange={(event) => setAffiliateEdit((state) => ({ ...state, syncToLinkTrust: event.target.checked }))} className="rounded bg-slate-700 border-slate-600" />Enable sync intent</label>
                    <label className="inline-flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={affiliateEdit.queueLinkTrustSync} onChange={(event) => setAffiliateEdit((state) => ({ ...state, queueLinkTrustSync: event.target.checked }))} className="rounded bg-slate-700 border-slate-600" />Queue outbound update</label>
                    <button onClick={saveAffiliateManagement} disabled={savingAffiliate} className="px-4 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-60 text-white rounded text-sm">{savingAffiliate ? 'Saving...' : 'Save Affiliate Settings'}</button>
                  </div>
                )
              )}

              {activeView === 'queue' && (
                <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3">
                  <h4 className="text-slate-100 text-sm font-semibold inline-flex items-center"><Settings2 className="w-4 h-4 mr-2 text-red-300" />Outbound Queue Controls</h4>
                  <p className="text-xs text-slate-500 mt-1">Use row actions in the queue list to mark sent/failed as integration handling evolves.</p>
                </div>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default Management;
